/**
 * O documento original na aba "Documentos" do painel da mesa única (E201.F2, GUS-47, ADR-0144 §8;
 * saiu do painel na E202.F1, quando ele ganhou a aba da prévia em PDF).
 *
 * Aqui se troca de arquivo, de página e de zoom. O PDF abre no visualizador do navegador (`#page` e
 * `#zoom` no endereço), e nele o texto se seleciona e copia; a imagem abre com zoom. O arquivo é o
 * original, pela rota que confere a custódia, e não é alterado.
 *
 * E201.F2.C01 (F-E201.F2-01): o arquivo pedido por "Ver na fonte" nunca é trocado por outro. Se ele
 * não abre aqui (um DOCX, por exemplo) ou não está mais no caso, a tela diz isso e oferece o
 * original do próprio arquivo pedido.
 */
import type { AxiosInstance } from 'axios';
import { useState } from 'react';

import { AbrirOriginal } from '@/components/FichaDaLeitura/AbrirOriginal';
import { FalhaComSaida } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { SourceDocumentRecordData } from '@/lib/case_source_documents';

import { ControlesDoDocumento, type DocumentoAberto } from './ControlesDoDocumento';
import {
  documentoDoPainel,
  ehPdf,
  nomeDoDocumento,
  useArquivoDoPainel,
  useDocumentosDoPainel,
} from './useDocumentoOriginal';

const t = ptBR.mesaUnica.documento;

export interface DocumentosDoPainelProps {
  client: AxiosInstance;
  caseId: string;
  aberto: DocumentoAberto;
  onMudar: (aberto: DocumentoAberto) => void;
}

function Visualizador({
  client,
  caseId,
  doc,
  pagina,
  zoom,
}: {
  client: AxiosInstance;
  caseId: string;
  doc: SourceDocumentRecordData;
  pagina: number;
  zoom: number;
}): JSX.Element {
  const [tentativa, setTentativa] = useState(0);
  const arquivo = useArquivoDoPainel(client, caseId, doc, tentativa);
  const quadro = t.quadro.replace('{nome}', nomeDoDocumento(doc));
  if (arquivo.estado === 'carregando') {
    return <p className="text-body-sm text-fg-muted">{t.carregando}</p>;
  }
  if (arquivo.estado === 'erro') {
    // E205.F4.T03: cada causa diz a sua; só a falha (rede, servidor) se tenta de novo.
    const falha = arquivo.motivo === 'falha';
    return (
      <FalhaComSaida
        mensagem={t.porQue[arquivo.motivo]}
        onTentarDeNovo={falha ? () => setTentativa((n) => n + 1) : undefined}
        data-testid="painel-documento-falha"
        data-motivo={arquivo.motivo}
      />
    );
  }
  if (ehPdf(doc)) {
    const endereco = `${arquivo.url}#page=${pagina}&zoom=${zoom}`;
    // A chave refaz o quadro a cada página ou zoom: o visualizador lê `#page` ao abrir o arquivo.
    return (
      <iframe
        key={endereco}
        title={quadro}
        src={endereco}
        className="h-[70vh] w-full border border-border bg-bg"
        data-testid="painel-documento-pdf"
      />
    );
  }
  return (
    <div className="h-[70vh] overflow-auto border border-border bg-bg">
      <img
        src={arquivo.url}
        alt={quadro}
        style={{ width: `${zoom}%` }}
        className="max-w-none"
        data-testid="painel-documento-imagem"
      />
    </div>
  );
}

/** O arquivo pedido que não abre no painel: o aviso e o original dele, nunca outro arquivo. */
function ForaDoPainel({
  client,
  caseId,
  pedido,
}: {
  client: AxiosInstance;
  caseId: string;
  pedido: SourceDocumentRecordData | undefined;
}): JSX.Element {
  const nome = pedido === undefined ? '' : nomeDoDocumento(pedido);
  return (
    <div className="space-y-sc-2" data-testid="painel-documento-fora">
      <p role="alert" className="text-body-sm text-fg">
        {pedido === undefined ? t.naoEncontrado : t.foraDoPainel.replace('{nome}', nome)}
      </p>
      {pedido !== undefined ? (
        <AbrirOriginal client={client} caseId={caseId} documentoId={pedido.id} nome={nome} />
      ) : null}
    </div>
  );
}

export function DocumentosDoPainel({ client, caseId, aberto, onMudar }: DocumentosDoPainelProps) {
  const [tentativa, setTentativa] = useState(0);
  const [zoom, setZoom] = useState(100);
  const lista = useDocumentosDoPainel(client, caseId, tentativa);
  if (lista.estado === 'carregando') {
    return <p className="text-body-sm text-fg-muted">{t.carregandoLista}</p>;
  }
  if (lista.estado === 'erro') {
    return (
      <FalhaComSaida
        mensagem={t.erroDaLista}
        onTentarDeNovo={() => setTentativa((n) => n + 1)}
        data-testid="painel-documento-falha-da-lista"
      />
    );
  }
  const docs = lista.documentos;
  const doc = documentoDoPainel(docs, aberto.documentoId);
  if (doc === undefined && aberto.documentoId === null) {
    return <p className="text-body-sm text-fg-muted">{t.semDocumentos}</p>;
  }
  return (
    <div className="space-y-sc-3">
      <label className="block text-body-sm text-fg">
        <span className="text-caption text-fg-muted">{t.arquivo}</span>
        <select
          className="mt-sc-1 block w-full border border-border bg-bg px-sc-2 py-sc-1 text-body-sm"
          value={doc?.id ?? ''}
          onChange={(e) => onMudar({ documentoId: e.target.value, pagina: 1 })}
          data-testid="painel-documento-arquivo"
        >
          {doc === undefined ? (
            <option value="" disabled>
              {t.escolha}
            </option>
          ) : null}
          {docs.map((d) => (
            <option key={d.id} value={d.id}>
              {nomeDoDocumento(d)}
              {d.page_count !== null && ehPdf(d) ? ` · ${d.page_count} p.` : ''}
            </option>
          ))}
        </select>
      </label>
      {doc === undefined ? (
        <ForaDoPainel
          client={client}
          caseId={caseId}
          pedido={lista.todos.find((d) => d.id === aberto.documentoId && d.deleted_at == null)}
        />
      ) : (
        <>
          <ControlesDoDocumento
            doc={doc}
            aberto={aberto}
            zoom={zoom}
            onMudar={onMudar}
            onZoom={setZoom}
          />
          <Visualizador
            client={client}
            caseId={caseId}
            doc={doc}
            pagina={aberto.pagina}
            zoom={zoom}
          />
          {ehPdf(doc) ? <p className="text-caption text-fg-muted">{t.dica}</p> : null}
        </>
      )}
    </div>
  );
}
